import Head from 'next/head'
import { useRouter } from 'next/router'

const DynamicHead = ({ aiFirstMeta }) => {
    const router = useRouter()

    /* AI-First Engineering routes: their meta is built at build time from the
       AI-First content model (Components/ai-first/seoMeta.js, called in each
       page's getStaticProps) and arrives here as pageProps.aiFirstMeta, so the
       content model never lands in the _app bundle. */
    const getAiFirstMeta = () => {
        const path = router.pathname || ''
        if (path !== '/ai-first-engineering' && !path.startsWith('/ai-first-engineering/')) {
            return null
        }
        return aiFirstMeta || null
    }

    // Check if this route should skip title (pages that handle their own titles)
    const shouldSkipTitle = () => {
        const asPath = router.asPath || ''
        const pathname = router.pathname || ''
        
        // Routes that should handle their own titles
        const skipTitlePatterns = [
            /^\/case-studies\/[^/]+$/, // Individual case studies (e.g., /case-studies/raisn)
            /^\/awards\/[^/]+$/, // Individual awards (e.g., /awards/prestigious-business-awards)
            /^\/products\/[^/]+$/, // Individual products (e.g., /products/integra-ai)
        ]
        
        // Check if asPath matches skip patterns
        for (const pattern of skipTitlePatterns) {
            if (pattern.test(asPath)) {
                return true
            }
        }
        
        // Check specific routes that should skip (exact matches)
        const skipRoutes = [
            '/awards', // Awards index page
        ]
        
        // Normalize paths for comparison (remove trailing slashes)
        const normalizedPathname = pathname.replace(/\/$/, '')
        const normalizedAsPath = asPath.split('?')[0].replace(/\/$/, '') // Remove query params and trailing slash
        
        if (skipRoutes.includes(normalizedPathname) || skipRoutes.includes(normalizedAsPath)) {
            return true
        }
        
        return false
    }

    const getMetaTags = () => {
        // AI-First Engineering routes get content-driven SEO (see above).
        const aiFirst = getAiFirstMeta()
        if (aiFirst) return aiFirst

        switch (router.pathname) {
            case '/services/ai-consulting-services':
                return {
                    title: 'AI Consulting Services | Expert AI Consultants for Business Growth',
                    description:
                        ' Get expert AI consulting services to optimize workflows, enhance decision-making, and drive innovation. Partner with top AI consultants for business transformation.',
                    keywords:
                        'ai consultant , AI consultants , ai consulting companies , Ai consulting services , AI Consultants , artificial intelligence consulting services.',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'AI Consulting Services | Expert AI Consultants for Business Growth',
                    ogImageAlt:'Expert AI Consultants for Business Growth',
                    ogDescription:
                        ' Get expert AI consulting services to optimize workflows, enhance decision-making, and drive innovation. Partner with top AI consultants for business transformation.',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle:
                        'AI Consulting Services | Expert AI Consultants for Business Growth',
                    twitterDescription:
                        ' Get expert AI consulting services to optimize workflows, enhance decision-making, and drive innovation. Partner with top AI consultants for business transformation.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }

            // case 'services/ai-development-services':
            //     return {
            //         title: 'Cloudastra — AI Software Development Company',
            //         description:
            //             'Cloudastra is a leading AI software development company offering AI development services, AI SaaS solutions, and AI consulting to drive innovation and growth.',
            //         keywords:
            //             'ai software development company , ai development companies, ai development services, ai software development, ai software development companies, ai saas, ai services, ai consulting services',
            //         viewport: 'width=device-width, initial-scale=1.0',
            //         ogTitle: 'Cloudastra — AI Software Development Company',
            //         ogDescription:
            //             'Cloudastra is a leading AI software development company offering AI development services, AI SaaS solutions, and AI consulting to drive innovation and growth.',
            //         ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
            //         ogUrl: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/services/ai-development-services`,
            //         ogUrl: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/services/ai-development-services`,
            //         ogType: 'website',
            //         ogImageAlt:'AI Software Development Company',
            //         ogSiteName: 'Cloudastra Technologies',
            //         twitterCard: 'summary_large_image',
            //         twitterTitle: 'Cloudastra — AI Software Development Company',
            //         twitterDescription:
            //             'Cloudastra is a leading AI software development company offering AI development services, AI SaaS solutions, and AI consulting to drive innovation and growth.',
            //         twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
            //     }

            case '/services/ai-as-a-service':
                return {
                    title: 'AI Services and Artificial Intelligence Development Solutions',
                    description:
                        'Cloudastra offers specialized AI services, delivering end-to-end cloud DevOps solutions, automated CI/CD pipelines, and performance optimization',
                    keywords:
                        ' ai service, artificial intelligence services, ai development service',
                    ogImageAlt:'ai service, artificial intelligence services',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'AI Services and Artificial Intelligence Development Solutions',
                    ogDescription:
                        'Cloudastra offers specialized AI services, delivering end-to-end cloud DevOps solutions, automated CI/CD pipelines, and performance optimization',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle: 'AI Services and Artificial Intelligence Development Solutions',
                    twitterDescription:
                        'Cloudastra offers specialized AI services, delivering end-to-end cloud DevOps solutions, automated CI/CD pipelines, and performance optimization',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }

            case '/services/cloud-and-devops':
                return {
                    title: 'DevOps and Cloud Computing Services',
                    description:
                        "Optimize your business with Cloudastra's DevOps and cloud computing solutions. Scalable, secure, and efficient cloud computing DevOps for seamless IT operations.",
                    keywords:
                        ' devops and cloud computing, cloud computing devops, devops and cloud computing, devops managed services.',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'DevOps and Cloud Computing Services',
                    ogImageAlt:'DevOps and Cloud Computing',
                    ogDescription:
                        'Optimize your business with Cloudastra DevOps and cloud computing solutions. Scalable, secure, and efficient cloud computing DevOps for seamless IT operations.',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle: 'DevOps and Cloud Computing Services',
                    twitterDescription:
                        'Optimize your business with Cloudastra DevOps and cloud computing solutions. Scalable, secure, and efficient cloud computing DevOps for seamless IT operations.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }

            case '/services/cloud-computing-managed-services':
                return {
                    title: 'Cloud Computing Services | Best Cloud Service Providers & Solution',
                    description:
                        'Cloudastra offers the best cloud computing services in the USA. As top cloud service providers, we offer secure, scalable, and cost-effective cloud solutions tailored to your business needs.',
                    keywords:
                        'cloud computing service providers, cloud service providers, cloud computing services, cloud based computing services, cloud computing providers, cloud computing companies, cloud solutions, cloud computing cost, hybrid cloud computing solutions, cloud computing security services, cloud based services, cloud consulting companies, best cloud computing services',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloud Computing Services | Best Cloud Service Providers & Solution',
                    ogDescription:
                        'Cloudastra offers the best cloud computing services in the USA. As top cloud service providers, we offer secure, scalable, and cost-effective cloud solutions tailored to your business needs.',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle:
                        'Cloud Computing Services | Best Cloud Service Providers & Solution',
                    twitterDescription:
                        'Cloudastra offers the best cloud computing services in the USA. As top cloud service providers, we offer secure, scalable, and cost-effective cloud solutions tailored to your business needs.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }

            case '/case-studies':
                return {
                    title: 'Cloudastra - Case Studies in Excellence',
                    description:
                        "Dive into Cloudastra's case studies, showcasing excellence in managed IT services, software consulting, and digital transformation solutions.",
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra - Case Studies in Excellence',
                    ogDescription:
                        "Dive into Cloudastra's case studies, showcasing excellence in managed IT services, software consulting, and digital transformation solutions.",
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle: 'Cloudastra - Case Studies in Excellence',
                    twitterDescription:
                        "Dive into Cloudastra's case studies, showcasing excellence in managed IT services, software consulting, and digital transformation solutions.",
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }

            case '/contact':
                return {
                    title: 'Cloudastra Technologies - Contact Us',
                    description:
                        'Your one- stop solution to built a strong online presence. Cloudastra provides devops & cloud, web application, ui- ux and mobile app development services.',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra Technologies - Contact Us',
                    ogDescription:
                        'Your one- stop solution to built a strong online presence. Cloudastra provides devops & cloud, web application, ui- ux and mobile app development services.',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle: 'Cloudastra Technologies - Contact Us',
                    twitterDescription:
                        'Your one- stop solution to built a strong online presence. Cloudastra provides devops & cloud, web application, ui- ux and mobile app development services.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }

            case '/products/nigmabot-ai':
                return {
                    title: "Nigmabot: Elevate Efficiency with Cloudastra's All-in-One Bot",
                    description:
                        'Discover Nigmabot by Cloudastra, your ultimate all-in-one bot solution. Streamline tasks, enhance productivity, and unlock the potential of automation. Explore the features and benefits of Nigmabot for seamless operations and optimized performance.',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: "Nigmabot: Elevate Efficiency with Cloudastra's All-in-One Bot",
                    ogDescription:
                        'Discover Nigmabot by Cloudastra, your ultimate all-in-one bot solution. Streamline tasks, enhance productivity, and unlock the potential of automation. Explore the features and benefits of Nigmabot for seamless operations and optimized performance.',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogType: 'website',
                    ogSiteName: 'Cloudastra Technologies',
                    twitterCard: 'summary_large_image',
                    twitterTitle: "Nigmabot: Elevate Efficiency with Cloudastra's All-in-One Bot",
                    twitterDescription:
                        'Discover Nigmabot by Cloudastra, your ultimate all-in-one bot solution. Streamline tasks, enhance productivity, and unlock the potential of automation. Explore the features and benefits of Nigmabot for seamless operations and optimized performance.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                }
            
            case '/services/ui-ux-design-services':
                return {
                    title: 'Cloudastra UI/UX design services and Consulting Solutions',
                    description:
                        'Transform user experiences with Cloudastra’s UI/UX design services—combining UX strategy, Responsive Design, AI development services, and UI/UX.',
                    keywords:
                        'UI/UX design services, Responsive Design, UI/UX Design, UI/UX Designers',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra UI/UX design services and Consulting Solutions',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Transform user experiences with Cloudastra’s UI/UX design services—combining UX strategy, Responsive Design, AI development services, and UI/UX.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'UI/UX design services',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloudastra UI/UX design services and Consulting Solutions',
                    twitterDescription:
                        'Transform user experiences with Cloudastra’s UI/UX design services—combining UX strategy, Responsive Design, AI development services, and UI/UX.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }
            
            case '/services/devops-as-a-service':
                return {
                    title: 'Cloudastra DevOps Services | Expert DevOps Consulting & Managed Solutions',
                    description:
                        'Optimize your software delivery with Cloudastra DevOps services. From DevOps consulting to managed services, we streamline automation, security, and scalability.',
                    keywords:
                        'devops services, devops consulting services, devops as a service, devops managed services ,devops services and solutions',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra DevOps Services | Expert DevOps Consulting & Managed Solutions',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Optimize your software delivery with Cloudastra DevOps services. From DevOps consulting to managed services, we streamline automation, security, and scalability.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'Expert DevOps Consulting & Managed Solutions',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloudastra DevOps Services | Expert DevOps Consulting & Managed Solutions',
                    twitterDescription:
                        'Optimize your software delivery with Cloudastra DevOps services. From DevOps consulting to managed services, we streamline automation, security, and scalability.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }

            case '/services/mvp-development-services':
                return {
                    title: 'MVP Development Services',
                    description:
                        'Cloudastra offers expert MVP development services to validate your idea, reduce risks, and accelerate time to market. Build a scalable MVP with our top MVP development company.',
                    keywords:
                        'mvp development services, mvp development company, mvp development agency, mvp services, mvp customer service',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'MVP Development Services',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Cloudastra offers expert MVP development services to validate your idea, reduce risks, and accelerate time to market. Build a scalable MVP with our top MVP development company.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'MVP Development Services',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'MVP Development Services',
                    twitterDescription:
                        'Cloudastra offers expert MVP development services to validate your idea, reduce risks, and accelerate time to market. Build a scalable MVP with our top MVP development company.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }

            case '/products/b2b-travel':
                return {
                    title: 'Cloudastra | Best B2B Booking Engine & Travel Management Software for Seamless Reservations',
                    description:
                        ' Cloudastra offers advanced B2B booking engine and travel management booking software for seamless flight, hotel, and tour reservations with real-time inventory and optimized operations',
                    keywords:
                        'Travel Management Booking Software ,best travel management software, travel management software, travel software development company, travel booking software',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra | Best B2B Booking Engine & Travel Management Software for Seamless Reservations',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                            ' Cloudastra offers advanced B2B booking engine and travel management booking software for seamless flight, hotel, and tour reservations with real-time inventory and optimized operations',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'Best B2B Booking Engine & Travel Management Software for Seamless Reservations',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloudastra | Best B2B Booking Engine & Travel Management Software for Seamless Reservations',
                    twitterDescription:
                            ' Cloudastra offers advanced B2B booking engine and travel management booking software for seamless flight, hotel, and tour reservations with real-time inventory and optimized operations',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }
            
            case '/services/cloud-cost-optimization':
                return {
                    title: 'Cloud Cost Optimization',
                    description:
                        'Optimize cloud spend with Cloudastra’s cloud cost optimization services—automated recommendations, cross-team analytics, and AWS management services now',
                    keywords:
                        'cloud cost optimization, cloud cost optimization services, cloud cost optimization strategies, cloud cost optimization best practices, cloud cost optimization services aws management services',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloud Cost Optimization',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Optimize cloud spend with Cloudastra’s cloud cost optimization services—automated recommendations, cross-team analytics, and AWS management services now',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'Cloud Cost Optimization',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloud Cost Optimization',
                    twitterDescription:
                        'Optimize cloud spend with Cloudastra’s cloud cost optimization services—automated recommendations, cross-team analytics, and AWS management services now',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }

            case '/services/custom-web-application-development-services':
                return {
                    title: 'Web Application Development Services',
                    description:
                    'Cloudastra offers custom web application development services, Web Development Services, and DevOps CI/CD to deliver scalable, cross-platform app solutions.',
                    keywords:
                        'web app development services​ app development services​ app development service cross platform app development services',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Web Application Development Services',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Cloudastra offers custom web application development services, Web Development Services, and DevOps CI/CD to deliver scalable, cross-platform app solutions.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'Web Application Development',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Web Application Development Services',
                    twitterDescription:
                        'Cloudastra offers custom web application development services, Web Development Services, and DevOps CI/CD to deliver scalable, cross-platform app solutions.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }
            
            case '/services/ai-development-services':
                return {
                    title: 'Cloudastra — AI Software Development Company',
                    description:
                        'Cloudastra is a leading AI software development company offering AI development services, AI SaaS solutions, and AI consulting to drive innovation and growth.',
                    keywords:
                        'ai software development company , ai development companies, ai development services, ai software development, ai software development companies, ai saas, ai services, ai consulting services',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra — AI Software Development Company',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Cloudastra is a leading AI software development company offering AI development services, AI SaaS solutions, and AI consulting to drive innovation and growth.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'AI Software Development Company',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloudastra — AI Software Development Company',
                    twitterDescription:
                        'Cloudastra is a leading AI software development company offering AI development services, AI SaaS solutions, and AI consulting to drive innovation and growth.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }
            
            case '/services/custom-web-development-services':
                return {
                    title: 'Cloudastra Custom Web Development for Success',
                    description:
                        'Accelerate growth with Cloudastra’s Custom Web Development Services—expert Frontend Development, Responsive Web Design, MVP & web app solutions.',
                    keywords:
                        'Frontend Development, Responsive Web Design, Web Development, Web Development for Startups, Web Development Services',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra Custom Web Development for Success',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Accelerate growth with Cloudastra’s Custom Web Development Services—expert Frontend Development, Responsive Web Design, MVP & web app solutions.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'Web Development for Success',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloudastra Custom Web Development for Success',
                    twitterDescription:
                        'Accelerate growth with Cloudastra’s Custom Web Development Services—expert Frontend Development, Responsive Web Design, MVP & web app solutions.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    
                }

           

            default:
                return {
                    title: 'Cloudastra - IT/Cloud Services For Every Need',
                    description:
                        'Boost your sales in the next  THE 6 months with Cloudastra Technologies top-rated IT/Cloud Services.',
                    keywords:
                        'cloud, technology, development, software, programming, web development, application development, mobile development, cloud infrastructure, cloud computing, IT, business technology, graphic designing, website design websites, website development company, web development company, web designing company, mobile app development, web design, graphics and design, app development, it services, mobile application development, website design company, website developer, web design agency, website designer near me, website designers, app developers, web designer near me, app development companies, mobile app developers, website design agency, mobile application development companies, mobile application developers, responsive web development, mobile development companies, best web design websites, mobile app dev, web page design company, ecommerce marketing, cloud devops, web design services, technology services, web application development, software development compay, IT/Cloud Services, website support and maintainance, front end development, react js development, flutter development, next.js development, backend development, django development, node js development, react developer, flutter developer, next js developer, backend developer, django developer, node js developer, website development company website, top website development company, website development costs, web development price, web development charges, blockchain technology',
                    lang: 'en',
                    viewport: 'width=device-width, initial-scale=1.0',
                    ogTitle: 'Cloudastra – IT/Cloud Services For Every Need',
                    ogSiteName: 'Cloudastra Technologies',
                    ogDescription:
                        'Boost your sales in the next THE 6 months with Cloudastra Technologies top-rated IT/Cloud Services.',
                    ogType: 'website',
                    ogImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    ogImageAlt: 'Tech Solution For Every Need',
                    ogImageType: 'image/png',
                    twitterCard: 'summary_large_image',
                    twitterSite: '@ Cloudastra Technologies',
                    twitterTitle: 'Cloudastra – IT/Cloud Services For Every Need',
                    twitterDescription:
                        'Boost your sales in the next 6 months with Cloudastra Technologies top-rated IT/Cloud Services.',
                    twitterImage: `${process.env.NEXT_PUBLIC_NEXTAUTH_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`,
                    googleSiteVerification: 'ZBj7C9zb5eRDtoc0ChXXj0cCwG9KNif2d3zHxRyF1ok',
                    pinterestDomainVerify: '30196e3480c2fbc3160bb4c3a1878144',
                }
        }
    }

    const metaTags = getMetaTags()
    const skipTitle = shouldSkipTitle()

    // Self-referencing canonical: env + current path, without query string or hash.
    // A page that is a duplicate of another can override it (and og:url / twitter:url)
    // with tags using the same keys in its own next/head. Error pages get none, and
    // blog pages (/blogs/*) still write their own unkeyed tags, so they get none here.
    const baseUrl = (process.env.NEXT_PUBLIC_NEXTAUTH_URL || '').replace(/\/$/, '')
    const asPath = (router.asPath || '/').split(/[?#]/)[0]
    const currentPath = asPath.startsWith('/') ? asPath : `/${asPath}`
    const isErrorPage = router.pathname === '/404' || router.pathname === '/_error'
    const isBlogPage = router.pathname === '/blogs' || router.pathname.startsWith('/blogs/')
    const canonicalHref = isErrorPage || isBlogPage
        ? null
        : `${baseUrl}${currentPath === '/' ? '' : currentPath.replace(/\/$/, '')}`

    return (
        <Head>
            {!skipTitle && <title>{metaTags.title}</title>}
            <meta name='description' content={metaTags.description} />
            {metaTags.keywords && <meta name='keywords' content={metaTags.keywords} />}
            <meta name='viewport' content={metaTags.viewport} />

            {/* Canonical URL for SEO */}
            {canonicalHref && <link key='canonical' rel='canonical' href={canonicalHref} />}

            {/* Keyed so a page's own og/twitter tags replace these defaults: next/head only
                de-duplicates <meta> by `name` or by an explicit key, never by `property`.
                Without keys both tags were emitted and crawlers took the first (this one). */}
            <meta key='og:title' property='og:title' content={metaTags.ogTitle || metaTags.title} />
            <meta
                key='og:description'
                property='og:description'
                content={metaTags.ogDescription || metaTags.description}
            />
            <meta key='og:image' property='og:image' content={metaTags.ogImage} />
            {canonicalHref && <meta key='og:url' property='og:url' content={canonicalHref} />}
            <meta key='og:type' property='og:type' content={metaTags.ogType} />
            <meta key='og:site_name' property='og:site_name' content={metaTags.ogSiteName} />
            {metaTags.ogImageAlt && (
                <meta key='og:image:alt' property='og:image:alt' content={metaTags.ogImageAlt} />
            )}
            {metaTags.ogImageType && (
                <meta key='og:image:type' property='og:image:type' content={metaTags.ogImageType} />
            )}

            <meta key='twitter:card' name='twitter:card' content={metaTags.twitterCard} />
            <meta
                key='twitter:title'
                name='twitter:title'
                content={metaTags.twitterTitle || metaTags.title}
            />
            <meta
                key='twitter:description'
                name='twitter:description'
                content={metaTags.twitterDescription || metaTags.description}
            />
            <meta key='twitter:image' name='twitter:image' content={metaTags.twitterImage} />
            {metaTags.twitterSite && (
                <meta key='twitter:site' name='twitter:site' content={metaTags.twitterSite} />
            )}
            {canonicalHref && <meta key='twitter:url' name='twitter:url' content={canonicalHref} />}

            {metaTags.lang && <meta name='lang' content={metaTags.lang} />}
            <meta name='google-site-verification' content={metaTags.googleSiteVerification} />
            {metaTags.pinterestDomainVerify && (
                <meta name='p:domain_verify' content={metaTags.pinterestDomainVerify} />
            )}
        </Head>
    )
}

export default DynamicHead
